import {Box} from '@chakra-ui/react'
import {useEffect, useState} from 'react'

/**
 * A dot marking a tab that has unsaved changes.
 *
 * Unlike a form-wide dirty flag, it watches the fields the tab itself renders:
 * every `FieldRoot` exposes `data-field-dirty` when its value differs from its
 * default, so each tab reports only its own changes.
 *
 * Usage:
 *   <Tabs.Root value={tab} onValueChange={setTab}>
 *     <Tabs.List>
 *       <Tabs.Trigger value="general">
 *         General
 *         <form.TabUnsavedIndicator value="general" />
 *       </Tabs.Trigger>
 *     </Tabs.List>
 *     <Tabs.Content value="general">...</Tabs.Content>
 *   </Tabs.Root>
 */

export interface TabUnsavedIndicatorProps {
  /** The `value` of the tab this dot belongs to. */
  value: string
  /** A change that isn't a form field, e.g. a pending logo upload. */
  forceVisible?: boolean
}

function hasDirtyField(tabValue: string): boolean {
  const panel = document.querySelector(`[role="tabpanel"][aria-labelledby$="trigger-${tabValue}"]`)
  if (!panel) return false

  return panel.querySelector('[data-field-dirty="true"]') !== null
}

export function TabUnsavedIndicator({value, forceVisible}: TabUnsavedIndicatorProps) {
  const [hasDirty, setHasDirty] = useState(Boolean(forceVisible) || hasDirtyField(value))

  useEffect(() => {
    const update = () => setHasDirty(Boolean(forceVisible) || hasDirtyField(value))
    update()

    // a field re-renders in isolation, and a tab panel mounts and unmounts as tabs are opened,
    // so observe the document rather than relying on a parent render
    const observer = new MutationObserver(update)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-field-dirty'],
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [value, forceVisible])

  return (
    <Box
      as="span"
      boxSize="2"
      borderRadius="full"
      bg="orange.solid"
      display="inline-block"
      opacity={hasDirty ? 1 : 0}
      aria-hidden="true"
    />
  )
}
